import type { TreatmentType } from "@src/packages/homepage/utils/costEstimator";

export type EstimateLineId =
  | "consult"
  | "female-workup"
  | "male-workup"
  | "uterine-cavity"
  | "egg-freezing-cycle"
  | "embryo-freezing-cycle"
  | "ivf-cycle"
  | "fet-cycle"
  | "iui-cycle"
  | "oi-cycle"
  | "sperm-freezing-cycle"
  | "anesthesia"
  | "medications-stimulation"
  | "medications-fet"
  | "medications-iui"
  | "medications-oi"
  | "icsi"
  | "pgta"
  | "pgtm-sr"
  | "pgtp"
  | "assisted-hatching"
  | "sperm-wash"
  | "donor-sperm"
  | "donor-eggs"
  | "donor-embryo"
  | "known-donor-coordination"
  | "storage"
  | "specimen-transfer"
  | "additional-fet"
  | "transfer-meds"
  | "extra-cycle-buffer";

export type CostSourceQuality =
  | "clinic-quote"
  | "clinic-published"
  | "regional-study"
  | "public-average"
  | "modeled";

export type CostPriceType =
  | "cash-pay"
  | "list-price"
  | "insurance-allowed"
  | "patient-paid"
  | "unknown";

export type CostMethodology =
  | "observed-clinic-price"
  | "nearby-clinic-average"
  | "regional-observed-average"
  | "national-public-reference"
  | "clinical-planning-model";

export type CostAvailabilityStatus =
  | "available"
  | "restricted"
  | "not-available"
  | "funded"
  | "partially-funded"
  | "unknown";

export type CostRestrictionReason =
  | "not-legal"
  | "not-offered"
  | "medical-criteria"
  | "age-limit"
  | "marital-status"
  | "residency"
  | "public-system-capacity"
  | "clinic-policy"
  | "unknown";

export type CostFundingModel =
  | "self-pay"
  | "insurance"
  | "public"
  | "mixed"
  | "employer-benefit"
  | "unknown";

export type CostLocationScope =
  | "clinic"
  | "city"
  | "state"
  | "country"
  | "global";

export type CostCatalogSource = {
  id: string;
  name: string;
  url?: string;
  publishedAt?: string;
  retrievedAt: string;
  quality: CostSourceQuality;
};

export type CostCatalogLocation = {
  scope: CostLocationScope;
  countryCode: string;
  stateCode?: string;
  city?: string;
  clinicId?: string;
  latitude?: number;
  longitude?: number;
};

export type CostAvailabilityPolicy = {
  status: CostAvailabilityStatus;
  fundingModel: CostFundingModel;
  restrictionReasons?: CostRestrictionReason[];
  fundedCyclesLimit?: number;
  fundedTransfersLimit?: number;
  ageLimitYears?: number;
  residencyRequired?: boolean;
  expectedWaitMonths?: {
    min: number;
    max: number;
  };
  privatePayAllowed?: boolean;
  crossBorderCommonDestinations?: string[];
  notes?: string;
  source?: CostCatalogSource;
};

export type CostCatalogEntry = {
  id: string;
  lineId: EstimateLineId;
  amount: number;
  range?: {
    low: number;
    high: number;
  };
  currency: "USD";
  unit: "once" | "cycle" | "embryo" | "storage-year" | "transfer";
  location: CostCatalogLocation;
  priceType: CostPriceType;
  methodology: CostMethodology;
  availability: CostAvailabilityPolicy;
  sampleSize?: number;
  appliesTo?: TreatmentType[];
  source: CostCatalogSource;
  effectiveFrom: string;
  lastReviewedAt: string;
  confidence: "high" | "medium" | "low";
};

export type FertilityCostCatalog = {
  version: string;
  effectiveFrom: string;
  generatedAt: string;
  defaultCurrency: "USD";
  notes: string[];
  sources: CostCatalogSource[];
  entries: CostCatalogEntry[];
};

const CARE_CREDIT_SOURCE: CostCatalogSource = {
  id: "carecredit-ivf-pricing-2025",
  name: "CareCredit IVF pricing overview",
  url: "https://www.carecredit.com/well-u/health-wellness/ivf-pricing/",
  publishedAt: "2025",
  retrievedAt: "2026-05-24",
  quality: "public-average",
};

const STATE_DIAGNOSTIC_SOURCE: CostCatalogSource = {
  id: "state-infertility-workup-costs-2024",
  name: "State-level infertility workup costs study",
  url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11705584/",
  publishedAt: "2024",
  retrievedAt: "2026-05-24",
  quality: "regional-study",
};

const MODELED_SOURCE: CostCatalogSource = {
  id: "clinical-planning-model-2026-05",
  name: "Clinical planning model",
  retrievedAt: "2026-05-24",
  quality: "modeled",
};

const POLICY_MODEL_SOURCE: CostCatalogSource = {
  id: "country-policy-model-2026-05",
  name: "Country access policy model",
  retrievedAt: "2026-05-24",
  quality: "modeled",
};

const US_COUNTRY_LOCATION: CostCatalogLocation = {
  scope: "country",
  countryCode: "US",
};

function usEntry(
  lineId: EstimateLineId,
  amount: number,
  unit: CostCatalogEntry["unit"],
  confidence: CostCatalogEntry["confidence"] = "medium",
  source: CostCatalogSource = CARE_CREDIT_SOURCE,
  range?: CostCatalogEntry["range"],
): CostCatalogEntry {
  return {
    id: `us-${lineId}`,
    lineId,
    amount,
    range,
    currency: "USD",
    unit,
    location: US_COUNTRY_LOCATION,
    priceType: "cash-pay",
    methodology:
      source.quality === "modeled"
        ? "clinical-planning-model"
        : source.quality === "regional-study"
          ? "regional-observed-average"
          : "national-public-reference",
    availability: {
      status: "available",
      fundingModel: "self-pay",
      source: source.quality === "modeled" ? POLICY_MODEL_SOURCE : source,
    },
    source,
    effectiveFrom: "2026-05-24",
    lastReviewedAt: "2026-05-24",
    confidence,
  };
}

function countryPolicyEntry({
  countryCode,
  lineId,
  availability,
}: {
  countryCode: string;
  lineId: EstimateLineId;
  availability: CostAvailabilityPolicy;
}): CostCatalogEntry {
  return {
    id: `${countryCode.toLowerCase()}-${lineId}-policy`,
    lineId,
    amount: availability.status === "funded" ? 0 : 0,
    currency: "USD",
    unit: lineId === "pgtp" || lineId === "pgta" ? "embryo" : "once",
    location: {
      scope: "country",
      countryCode,
    },
    priceType:
      availability.status === "funded" ||
      availability.status === "partially-funded"
        ? "patient-paid"
        : "unknown",
    methodology: "clinical-planning-model",
    availability,
    source: availability.source ?? POLICY_MODEL_SOURCE,
    effectiveFrom: "2026-05-24",
    lastReviewedAt: "2026-05-24",
    confidence: "low",
  };
}

export const FERTILITY_COST_CATALOG: FertilityCostCatalog = {
  version: "2026.05.24-schema-seed",
  effectiveFrom: "2026-05-24",
  generatedAt: "2026-05-24",
  defaultCurrency: "USD",
  notes: [
    "This is a schema seed, not comprehensive coverage.",
    "Clinic, city, state, and global rows should be ingested as observed price facts rather than added ad hoc in UI code.",
    "Country rows are fallbacks only and should lose priority as clinic and regional coverage improves.",
  ],
  sources: [
    CARE_CREDIT_SOURCE,
    STATE_DIAGNOSTIC_SOURCE,
    MODELED_SOURCE,
    POLICY_MODEL_SOURCE,
  ],
  entries: [
    usEntry("consult", 220, "once", "low", MODELED_SOURCE, {
      low: 0,
      high: 500,
    }),
    usEntry("female-workup", 650, "once", "medium", STATE_DIAGNOSTIC_SOURCE),
    usEntry("male-workup", 250, "once", "medium", STATE_DIAGNOSTIC_SOURCE),
    usEntry("uterine-cavity", 950, "once", "medium", STATE_DIAGNOSTIC_SOURCE),
    usEntry("egg-freezing-cycle", 8800, "cycle"),
    usEntry("embryo-freezing-cycle", 10500, "cycle"),
    usEntry("ivf-cycle", 11200, "cycle"),
    usEntry("fet-cycle", 4500, "transfer"),
    usEntry("iui-cycle", 2000, "cycle"),
    usEntry("oi-cycle", 1600, "cycle", "low", MODELED_SOURCE),
    usEntry("sperm-freezing-cycle", 1000, "cycle"),
    usEntry("anesthesia", 750, "cycle"),
    usEntry("medications-stimulation", 3991, "cycle"),
    usEntry("medications-fet", 750, "transfer"),
    usEntry("medications-iui", 775, "cycle"),
    usEntry("medications-oi", 152, "cycle"),
    usEntry("icsi", 1500, "cycle"),
    usEntry("pgta", 650, "embryo", "low", MODELED_SOURCE),
    usEntry("pgtm-sr", 3200, "once", "low", MODELED_SOURCE),
    usEntry("pgtp", 800, "embryo", "low", MODELED_SOURCE),
    usEntry("assisted-hatching", 650, "cycle"),
    usEntry("sperm-wash", 350, "cycle"),
    usEntry("donor-sperm", 1200, "cycle", "low", MODELED_SOURCE),
    usEntry("donor-eggs", 21000, "once", "low", MODELED_SOURCE),
    usEntry("donor-embryo", 3000, "once", "low", MODELED_SOURCE),
    usEntry("known-donor-coordination", 2500, "once", "low", MODELED_SOURCE),
    usEntry("storage", 950, "storage-year", "low", MODELED_SOURCE),
    usEntry("specimen-transfer", 650, "once", "low", MODELED_SOURCE),
    usEntry("additional-fet", 4800, "transfer", "low", MODELED_SOURCE),
    usEntry("transfer-meds", 500, "transfer", "low", MODELED_SOURCE),
    usEntry("extra-cycle-buffer", 11200, "once", "low", MODELED_SOURCE),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "ivf-cycle",
      availability: {
        status: "partially-funded",
        fundingModel: "public",
        restrictionReasons: [
          "medical-criteria",
          "residency",
          "public-system-capacity",
        ],
        residencyRequired: true,
        expectedWaitMonths: { min: 6, max: 24 },
        privatePayAllowed: true,
        crossBorderCommonDestinations: ["ES", "BE"],
        notes:
          "Placeholder policy row: public funding can reduce patient-paid cost, but wait time and eligibility may drive private or cross-border care.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "pgtp",
      availability: {
        status: "not-available",
        fundingModel: "unknown",
        restrictionReasons: ["not-legal"],
        privatePayAllowed: false,
        crossBorderCommonDestinations: ["ES"],
        notes:
          "Placeholder policy row: polygenic embryo screening availability must be verified before surfacing this option.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "DE",
      lineId: "pgtp",
      availability: {
        status: "not-available",
        fundingModel: "unknown",
        restrictionReasons: ["not-legal"],
        privatePayAllowed: false,
        crossBorderCommonDestinations: ["ES", "CZ"],
        notes:
          "Placeholder policy row: embryo genetic testing rules vary by indication and require country-specific review.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "ES",
      lineId: "pgtp",
      availability: {
        status: "restricted",
        fundingModel: "self-pay",
        restrictionReasons: ["clinic-policy"],
        privatePayAllowed: true,
        notes:
          "Placeholder policy row: private-pay cross-border care may be faster, but exact test availability depends on clinic and lab policy.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
  ],
};
